import { useEffect, useMemo, useRef, useState } from "react";
import SiteHeader from "@/components/suffa/SiteHeader";
import { OrnamentDivider, LogoMark } from "@/components/suffa/Ornaments";
import { calculateAgeParts } from "@/lib/profiles";
import { getDivisionLabel } from "@/lib/locations";
import { STEPS, INITIAL_DATA } from "@/lib/profileForm";
import { useLanguage } from "@/lib/i18n";
import { useAuth } from "@/lib/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { StepProfileFor, StepBasic, StepCareer } from "@/components/suffa/profile/StepsPart1";
import {
  StepReligious,
  StepLifestyle,
  StepFamily,
  StepPreferences,
} from "@/components/suffa/profile/StepsPart2";
import { StepPhotos, StepContact, ProfilePreview, } from "@/components/suffa/profile/StepsPart3";
import SubmissionSuccess from "@/components/suffa/profile/SubmissionSuccess";
import "@/pages/CreateProfile.css";

const DRAFT_KEY = "suffa_profile_draft_v1";

export default function CreateProfile() {
  const hasDraft = (d) => !!(d && (d.profileFor || d.fullName));

  const [boot] = useState(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (hasDraft(saved)) {
          return {
            data: {
              ...INITIAL_DATA,
              ...saved,
              communityJamaat: Array.isArray(saved.communityJamaat)
                ? saved.communityJamaat[0] || ""
                : saved.communityJamaat,
              mainPhoto: null,
            },
            restored: true,
          };
        }
      }
    } catch (e) {
      /* ignore corrupt drafts */
    }
    return { data: INITIAL_DATA, restored: false };
  });
  const [step, setStep] = useState(1);
  const [data, setData] = useState(boot.data);
  const [errors, setErrors] = useState({});
  const [draftRestored, setDraftRestored] = useState(boot.restored);
  const [submitted, setSubmitted] = useState(false);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const topRef = useRef(null);
  const { t } = useLanguage();
  const { user, authReady, refreshUser } = useAuth();

  useEffect(() => {
    document.title = "Create Your Profile | Suffa Matrimony";
  }, []);

  // Load the saved profile from the backend (edit mode) once authenticated.
  useEffect(() => {
    if (!authReady || !user) return;
    (async () => {
      try {
        const d = await api.get("/profiles/me");
        if (d?.profile?.data) {
          setData({ ...INITIAL_DATA, ...d.profile.data, mainPhoto: null });
          setEditing(true);
        }
      } catch (e) {
        /* no saved profile yet — continue with the local draft */
      }
    })();
  }, [authReady, user]);

  useEffect(() => {
    if (submitted || editing) return;
    const { mainPhoto, mainPhotoError, ...rest } = data;
    try {
      if (hasDraft(rest)) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(rest));
      } else {
        localStorage.removeItem(DRAFT_KEY);
      }
    } catch (e) {
      /* storage may be full or blocked */
    }
  }, [data, submitted, editing]);

  const set = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validators = useMemo(
    () => ({
      1: (d) => (!d.profileFor ? { profileFor: "Please choose who this profile is for." } : {}),
      2: (d) => {
        const e = {};
        if (!d.fullName.trim() || d.fullName.trim().length < 3)
          e.fullName = "Please enter the full name (at least 3 characters).";
        if (!d.gender) e.gender = "Please select the gender.";
        const { day, month, year } = d.dob || {};
        if (!day || !month || !year) {
          e.dob = "Please provide the complete date of birth (day, month and year).";
        } else {
          const parts = calculateAgeParts(d.dob);
          if (!parts) e.dob = "Please provide a valid date of birth — it cannot be in the future.";
          else if (parts.years < 18)
            e.dob = "Profiles can be created for adults aged 18 and above.";
        }
        if (!d.country) e.country = "Please select a country.";
        if (!d.state)
          e.state = d.country
            ? `Please select the ${getDivisionLabel(d.country).toLowerCase()}.`
            : "Please select a state / province.";
        if (!d.city) e.city = "Please select the city.";
        if (!d.nationality) e.nationality = "Please select the nationality.";
        if (!d.communityJamaat)
          e.communityJamaat = "Please select your Community / Jamaat.";
        if (d.communityJamaat === "Other" && !d.communityJamaatOther.trim())
          e.communityJamaatOther = "Please enter your community / jamaat.";
        if (!d.maritalStatus) e.maritalStatus = "Please select the marital status.";
        if (d.maritalStatus && d.maritalStatus !== "Never Married" && !d.children)
          e.children = "Please select the number of children.";
        return e;
      },
      3: (d) => {
        const e = {};
        if (!d.schoolEducation) e.schoolEducation = "Please select your school education.";
        if (d.schoolEducation === "Other" && !d.schoolEducationOther.trim())
          e.schoolEducationOther = "Please enter your school education.";
        if (!d.madarsaEducation) e.madarsaEducation = "Please select your madarsa education.";
        if (d.madarsaEducation === "Other" && !d.madarsaEducationOther.trim())
          e.madarsaEducationOther = "Please enter your madarsa education.";
        if (!d.profession) e.profession = "Please select the profession.";
        if (d.profession === "Other" && !d.professionOther.trim())
          e.professionOther = "Please enter your profession.";
        return e;
      },
      4: (d) => {
        const e = {};
        if ((d.schoolOfThought || []).includes("Other") && !d.schoolOfThoughtOther.trim())
          e.schoolOfThoughtOther = "Please enter your school of thought / religious affiliation.";
        if (d.religiousPractice === "Other" && !d.religiousPracticeOther.trim())
          e.religiousPracticeOther = "Please specify your religious practice.";
        return e;
      },
      5: (d) => {
        const e = {};
        if (d.height) {
          const h = Number(d.height);
          if (Number.isNaN(h) || h < 120 || h > 210)
            e.height = "Please enter a height between 120 and 210 cm.";
        }
        if (d.weight) {
          const w = Number(d.weight);
          if (Number.isNaN(w) || w < 20 || w > 300)
            e.weight = "Please enter a weight between 20 and 300 kg.";
        }
        if (d.complexion === "Other" && !d.complexionOther.trim())
          e.complexionOther = "Please enter your complexion / skin tone.";
        if ((d.languages || []).includes("Other") && !d.languagesOther.trim())
          e.languagesOther = "Please enter your language.";
        return e;
      },
      6: () => ({}),
      7: (d) => {
        const e = {};
        if (!d.prefAgeFrom) e.prefAgeFrom = "Please select the preferred age from.";
        if (!d.prefAgeTo) e.prefAgeTo = "Please select the preferred age to.";
        if (d.prefAgeFrom && d.prefAgeTo && Number(d.prefAgeFrom) > Number(d.prefAgeTo))
          e.prefAgeTo = "Preferred age to should be greater than age from.";
        if (!(d.prefLocations || []).length)
          e.prefLocations = "Please select at least one preferred country / location.";
        if (!(d.prefMaritalStatuses || []).length)
          e.prefMaritalStatuses = "Please select at least one preferred marital status.";
        return e;
      },
      8: (d) => {
        const e = {};
        if (!d.mainPhoto && !d.mainPhotoUrl)
          e.mainPhoto = "Please upload a main profile photo.";
        if (!d.photoPrivacy) e.photoPrivacy = "Please select the photo privacy.";
        return e;
      },
      9: (d) => {
        const e = {};
        const digits = (d.mobile || "").replace(/\D/g, "");
        if (!d.mobile.trim()) e.mobile = "Please enter the mobile number.";
        else if (digits.length < 8 || digits.length > 15)
          e.mobile = "Please enter a valid mobile number (8–15 digits).";
        if (d.email.trim() && !/^\S+@\S+\.\S+$/.test(d.email.trim()))
          e.email = "Please enter a valid email address.";
        if (!d.waliName.trim()) e.waliName = "Please enter Parent / Wali name.";
        if (!d.waliRelationship) e.waliRelationship = "Please select the relationship.";
        const wDigits = (d.waliMobile || "").replace(/\D/g, "");
        if (!d.waliMobile.trim()) e.waliMobile = "Please enter Parent / Wali mobile number.";
        else if (wDigits.length < 8 || wDigits.length > 15)
          e.waliMobile = "Please enter a valid Parent / Wali mobile number (8–15 digits).";
        if (!d.fullAddress.trim()) e.fullAddress = "Please enter your full current address.";
        return e;
      },
    }),
    [],
  );

  const scrollTop = () => {
    requestAnimationFrame(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const next = () => {
    const e = (validators[step] || (() => ({})))(data);
    setErrors(e);
    if (Object.keys(e).length === 0 && step < 10) {
      setStep(step + 1);
    }
    scrollTop();
  };

  const back = () => {
    setErrors({});
    setStep((s) => Math.max(1, s - 1));
    scrollTop();
  };

  const goTo = (n) => {
    setErrors({});
    setStep(n);
    scrollTop();
  };

  const submit = async () => {
    setSubmitError("");
    setSubmitting(true);
    try {
      await api.put("/profiles/me", {
        data: { ...data, mainPhoto: null, mainPhotoError: undefined },
      });
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch (e) {
        /* ignore */
      }
      await refreshUser();
      setSubmitted(true);
      scrollTop();
    } catch (e) {
      setSubmitError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const currentTitle = STEPS[step - 1].title;
  const pct = Math.round(((step - 1) / (STEPS.length - 1)) * 100);

  if (!authReady) {
    return (
      <div className="suffa-page">
        <SiteHeader />
        <main className="cp-main">
          <div className="cp-container" style={{ padding: "100px 0", textAlign: "center" }}>
            <p className="cp-subtitle" data-testid="cp-loading">{t("Loading…")}</p>
          </div>
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="suffa-page">
        <SiteHeader />
        <main className="cp-main">
          <div className="cp-container" style={{ padding: "60px 0 120px" }}>
            <section
              className="cp-card"
              style={{ maxWidth: 560, margin: "0 auto", textAlign: "center" }}
              data-testid="cp-login-required"
            >
              <OrnamentDivider />
              <h2 className="cp-step-title" data-testid="cp-login-required-title">
                {t("Login Required")}
              </h2>
              <p className="cp-subtitle" style={{ marginBottom: 24 }}>
                {t(
                  "Your matrimonial profile is saved securely with your Suffa Matrimony account. Please login or create an account to continue.",
                )}
              </p>
              <div className="cp-actions" style={{ justifyContent: "center" }}>
                <a href="/login" className="btn btn-outline btn-lg" data-testid="cp-gate-login">
                  {t("Login")}
                </a>
                <a href="/register" className="btn btn-solid btn-lg" data-testid="cp-gate-register">
                  {t("Register")}
                </a>
              </div>
            </section>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="suffa-page">
      <SiteHeader />
      <main className="cp-main">
        {submitted ? (
          <SubmissionSuccess data={data} />
        ) : (
          <>
            <section className="cp-masthead" ref={topRef}>
              <div className="cp-container cp-masthead-inner">
                <OrnamentDivider />
                <h1 className="cp-title" data-testid="cp-title">
                  {t("Create Your Matrimony Profile")}
                </h1>
                <p className="cp-subtitle" data-testid="cp-subtitle">
                  {t("Tell us about yourself and what you are looking for in a life partner.")}
                </p>
              </div>
            </section>

            <div className="cp-container">
              <div className="cp-progress" data-testid="cp-progress">
                <div className="cp-progress-head">
                  <span data-testid="cp-step-label">
                    Step {step} {t("of")} {STEPS.length} — {t(currentTitle)}
                  </span>
                  <span>{pct}%</span>
                </div>
                <div className="cp-bar">
                  <div className="cp-bar-fill" style={{ width: `${Math.max(pct, 4)}%` }} />
                </div>
              </div>

              {draftRestored && !editing && step === 1 && (
                <div className="cp-draft-note" data-testid="cp-draft-note">
                  {t("We restored your saved draft.")}
                  <button
                    type="button"
                    data-testid="cp-draft-clear"
                    onClick={() => {
                      setData({ ...INITIAL_DATA });
                      setDraftRestored(false);
                      try {
                        localStorage.removeItem(DRAFT_KEY);
                      } catch (e) {
                        /* ignore */
                      }
                    }}
                  >
                    {t("Start over")}
                  </button>
                </div>
              )}

              <section className="cp-card">
                <h2 className="cp-step-title" data-testid="cp-step-title">
                  {t(currentTitle)}
                </h2>

                {step === 1 && <StepProfileFor data={data} set={set} errors={errors} />}
                {step === 2 && <StepBasic data={data} set={set} errors={errors} />}
                {step === 3 && <StepCareer data={data} set={set} errors={errors} />}
                {step === 4 && <StepReligious data={data} set={set} errors={errors} />}
                {step === 5 && <StepLifestyle data={data} set={set} errors={errors} />}
                {step === 6 && <StepFamily data={data} set={set} errors={errors} />}
                {step === 7 && <StepPreferences data={data} set={set} errors={errors} />}
                {step === 8 && <StepPhotos data={data} set={set} errors={errors} />}
                {step === 9 && <StepContact data={data} set={set} errors={errors} />}
                {step === 10 && <ProfilePreview data={data} onEdit={goTo} />}

                {submitError && (
                  <p className="cp-error" data-testid="cp-submit-error" role="alert">
                    {t(submitError)}
                  </p>
                )}

                <div className="cp-actions">
                  {step > 1 ? (
                    <button
                      type="button"
                      className="btn btn-outline btn-lg"
                      data-testid="cp-back"
                      onClick={back}
                    >
                      {t("Back")}
                    </button>
                  ) : (
                    <span />
                  )}
                  {step < 10 ? (
                    <button
                      type="button"
                      className="btn btn-solid btn-lg"
                      data-testid="cp-continue"
                      onClick={next}
                    >
                      {t("Continue")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-solid btn-lg"
                      data-testid="cp-submit"
                      onClick={submit}
                      disabled={submitting}
                    >
                      {submitting ? t("Submitting…") : t("Submit Profile")}
                    </button>
                  )}
                </div>

                <p className="cp-save-note" data-testid="cp-save-note">
                  <LogoMark size={16} />
                  {t(
                    "Your progress is saved as you complete your profile. Your submitted profile is securely stored with your account and shown in Find Matches only after it passes Suffa Matrimony's approval and privacy rules.",
                  )}
                </p>
              </section>
            </div>
          </>
        )}
        <div className="cp-footer-space" />
      </main>
    </div>
  );
}
